<script>
export default {
  name: "ModalViewSquadPlayer",
  props: {
    player: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showModal: false,
    };
  },
  computed: {
    displayName() {
      const p = this.player;
      if (!p) return "";
      if (p.person_name) return p.person_name;
      const fn = p.first_name || "";
      const ln = p.last_name || "";
      return `${fn} ${ln}`.trim();
    },
    statTiles() {
      const p = this.player;
      const stats = (p && p.stats) || {};
      const val = (k) => this.formatStat(stats[k]);
      return [
        {
          label: "Matches Played",
          value: val("Matches Played"),
          icon: "mdi mdi-run",
        },
        {
          label: "Time Played",
          value: val("Time Played"),
          icon: "mdi mdi-alarm",
        },
        {
          label: "Goals",
          value: val("Goals"),
          icon: "mdi mdi-soccer",
        },
        {
          label: "Assists",
          value: val("Assists"),
          icon: "mdi mdi-shoe-cleat",
        },
        {
          label: "Yellow Cards",
          value: val("Yellow Cards"),
          iconClass: "stat-icon-card stat-icon-card--yellow",
        },
        {
          label: "Red Cards",
          value: val("Red Cards"),
          iconClass: "stat-icon-card stat-icon-card--red",
        },
      ];
    },
    showStatsSection() {
      const p = this.player;
      return p && (p.show_stats === "Y" || p.show_stats === true || p.stats);
    },
    shirtLabel() {
      const p = this.player;
      if (!p || p.shirt_number === null || p.shirt_number === undefined || p.shirt_number === "")
        return "—";
      return `#${p.shirt_number}`;
    },
  },
  methods: {
    formatStat(v) {
      if (v === null || v === undefined || v === "") return "0";
      return String(v);
    },
    formatBirthdate(val) {
      if (!val) return "—";
      const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return val;
      const d = parseInt(m[3], 10);
      const mo = parseInt(m[2], 10);
      const y = m[1];
      return `${d}-${mo}-${y}`;
    },
    closeModal() {
      this.showModal = false;
    },
    /** Called from parent after `viewPlayer` prop is set */
    open() {
      this.showModal = true;
    },
  },
};
</script>

<template>
  <b-modal
    id="squad_view_player"
    v-model="showModal"
    header-class="p-0 border-0 rounded-top"
    body-class="p-0"
    content-class="border-0 overflow-hidden squad-player-view-content"
    dialog-class="squad-player-view-dialog"
    centered
    size="md"
    @hidden="$emit('hidden')"
  >
    <template v-slot:modal-header>
      <div class="squad-player-view__header-inner w-100 d-flex align-items-center px-3 py-3">
        <button
          type="button"
          class="btn btn-link squad-player-view__back text-white text-decoration-none p-0 shadow-none"
          aria-label="Close"
          @click="closeModal"
        >
          <i class="uil uil-arrow-left font-size-22"></i>
        </button>
        <span class="squad-player-view__title text-white flex-grow-1 text-center text-uppercase fw-semibold">
          Teams
        </span>
        <button
          type="button"
          class="btn-close btn-close-white opacity-100"
          aria-label="Close"
          @click="closeModal"
        />
      </div>
    </template>

    <div v-if="player" class="squad-player-view">
      <div class="squad-player-view__body px-3 pb-4 pt-3">
        <div class="squad-player-view__card bg-white rounded-3 shadow-sm overflow-hidden">
          <div class="d-flex p-3 squad-player-view__hero position-relative">
            <div class="squad-player-view__photo-wrap flex-shrink-0">
              <img
                v-if="player.img"
                :src="player.img"
                :alt="displayName"
                class="squad-player-view__photo img-fluid rounded-3"
              />
              <img
                v-else
                src="@/assets/images/image_not_found.png"
                :alt="displayName"
                class="squad-player-view__photo img-fluid rounded-3"
              />
            </div>
            <div class="squad-player-view__meta flex-grow-1 ps-3 pe-5">
              <h5 class="squad-player-view__name fw-bold text-dark mb-1">
                {{ displayName }}
              </h5>
              <div class="squad-player-view__function text-muted mb-2">
                {{ player.function || "—" }}
              </div>
              <div class="d-flex align-items-center text-muted small mb-1" v-if="player.nation">
                <i class="mdi mdi-earth me-2"></i>
                <span>{{ player.nation }}</span>
              </div>
              <div class="d-flex align-items-center text-muted small" v-if="player.birthdate">
                <i class="mdi mdi-cake me-2"></i>
                <span>{{ formatBirthdate(player.birthdate) }}</span>
              </div>
            </div>
            <div class="squad-player-view__shirt squad-player-view__shirt--corner fw-bold">
              {{ shirtLabel }}
            </div>
          </div>

          <template v-if="showStatsSection">
            <div class="squad-player-view__divider mx-3"></div>
            <div class="p-3">
              <div class="squad-player-view__stat-grid">
                <div
                  v-for="(tile, idx) in statTiles"
                  :key="idx"
                  class="squad-player-view__stat-cell text-center"
                >
                  <div class="squad-player-view__stat-icon-wrap mb-2">
                    <i v-if="tile.icon" :class="tile.icon"></i>
                    <span v-else-if="tile.iconClass" :class="tile.iconClass"></span>
                  </div>
                  <div class="squad-player-view__stat-label small text-muted mb-1">
                    {{ tile.label }}
                  </div>
                  <div class="squad-player-view__stat-value fw-semibold text-primary">
                    {{ tile.value }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <b-button variant="light" class="text-dark" @click="closeModal">Close</b-button>
    </template>
  </b-modal>
</template>

<style scoped>
.squad-player-view-content {
  border-radius: 12px;
}

.squad-player-view__header-inner {
  background: linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%);
}

.squad-player-view__body {
  background: #f1f5f9;
}

.squad-player-view__photo {
  width: 152px;
  height: 310px;
  object-fit: cover;
}

.squad-player-view__shirt--corner {
  position: absolute;
  right: 16px;
  bottom: 16px;
  font-size: 2.15rem;
  line-height: 1;
  color: #2563eb;
}

.squad-player-view__divider {
  height: 1px;
  background: #e2e8f0;
}

.squad-player-view__stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem 0.75rem;
  padding-bottom: 130px;
}

.squad-player-view__stat-icon-wrap {
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 1.25rem;
}

.squad-player-view__stat-value {
  font-size: 1.05rem;
}

.stat-icon-card {
  display: inline-block;
  width: 18px;
  height: 30px;
  border-radius: 3px;
}

.stat-icon-card--yellow {
  background: linear-gradient(180deg, #facc15, #eab308);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transform: rotate(12deg);
}

.stat-icon-card--red {
  background: linear-gradient(180deg, #f87171, #dc2626);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transform: rotate(12deg);
}
</style>
